/**
 * **Add visit ▾** — one button, five starts (visits §5.1.1, Board 10; phone
 * Board 17 cell 1).
 *
 * A 420px list hanging off the button — a list, not an editor, so it is not
 * the 480 popover — each row a 28px tile, a name and one line of help, in the
 * contract's order. On a phone the same five rows are a sheet, `Add a visit`.
 *
 * *Copy of a visit…* replaces the rows with this quote's visits (name, the
 * When phrase, the total); picking one copies it directly below its source.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { QuoteVisitView } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDollars } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import { Popover } from '../components/overlays.js'
import { PanelSheet } from '../components/panel-sheet.js'
import {
  IconChevronLeft, IconChevronRight, IconFile, IconLayers, IconPackage, IconVisitDay,
} from '../frame/icons.js'
import { IconService } from '../builder/rows/glyphs.js'
import { ADD_VISIT_MENU, ADD_VISIT_SHEET } from '../builder/copy.js'

export type AddVisitStart = 'blank' | 'service' | 'package' | 'copy' | 'other'

const GLYPH: Record<AddVisitStart, ReactNode> = {
  blank: <IconVisitDay size={15} />,
  service: <IconService size={15} />,
  package: <IconPackage size={15} />,
  copy: <IconLayers size={15} />,
  other: <IconFile size={15} />,
}

export function AddVisitMenu({
  open, anchor, onClose, onStart, visits, onCopy, canCopyFromQuote = false,
}: {
  open: boolean
  anchor: HTMLElement | null
  onClose: () => void
  onStart: (start: Exclude<AddVisitStart, 'copy'>) => void
  visits: QuoteVisitView[]
  /** Copy of a visit… → this visit, directly below itself. */
  onCopy: (visitId: string) => void
  /**
   * Whether *Copy from another quote…* has a flow to call. Today's copy is
   * whole quotes only (§5.1.2), so it is drawn and not offered — step-15.md
   * DEVIATION REQUEST.
   */
  canCopyFromQuote?: boolean
}) {
  const phone = usePhone()
  const [picking, setPicking] = useState(false)
  useEffect(() => { if (!open) setPicking(false) }, [open])

  const starts = (['blank', 'service', 'package', 'copy', 'other'] as const).map((start) => {
    const disabled = (start === 'copy' && visits.length === 0) || (start === 'other' && !canCopyFromQuote)
    return (
      <TileMenuRow
        key={start}
        phone={phone}
        glyph={GLYPH[start]}
        label={ADD_VISIT_MENU[start].label}
        help={ADD_VISIT_MENU[start].help}
        disabled={disabled}
        onClick={() => {
          if (start === 'copy') { setPicking(true); return }
          onClose()
          onStart(start)
        }}
      />
    )
  })

  const list = visits.map((visit) => (
    <TileMenuRow
      key={visit.id}
      phone={phone}
      glyph={GLYPH.blank}
      label={visit.name || 'Untitled visit'}
      help={visit.phrase.card}
      trailing={<span className="tnum shrink-0 text-[12.5px] font-semibold text-text">{formatDollars(visit.total)}</span>}
      onClick={() => { onClose(); onCopy(visit.id) }}
    />
  ))

  if (phone) {
    return (
      <PanelSheet
        open={open}
        onClose={onClose}
        title={picking ? ADD_VISIT_MENU.copy.label : ADD_VISIT_SHEET.title}
        sub={picking ? undefined : ADD_VISIT_SHEET.sub}
        onBack={picking ? () => setPicking(false) : undefined}
        backLabel={picking ? ADD_VISIT_SHEET.title : undefined}
        layer={70}
      >
        <div className="flex flex-col">{picking ? list : starts}</div>
      </PanelSheet>
    )
  }

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} width={420} label={ADD_VISIT_SHEET.title}
      className="overflow-hidden rounded-[12px] p-0">
      {picking && (
        <button type="button" onClick={() => setPicking(false)}
          className="flex h-9 w-full items-center gap-1 border-b border-line-faint px-3 text-[12.5px] font-semibold text-accent">
          <IconChevronLeft size={14} /> {ADD_VISIT_MENU.copy.label}
        </button>
      )}
      <div className="flex flex-col">{picking ? list : starts}</div>
    </Popover>
  )
}

/**
 * Board 10's row: a 28px tile, the name at 13.5/600, one line of help — 56px
 * with a 36px tile and a chevron on a phone.
 *
 * Exported because **New billing plan ▾** is the same menu over five plan
 * kinds (Aaron, review item 5.1): one row, so the two menus on the builder's
 * two toolbars cannot drift apart in spacing, type or tile.
 */
export function TileMenuRow({ glyph, label, help, onClick, disabled, phone, trailing }: {
  glyph: ReactNode
  label: string
  help: string
  onClick: () => void
  disabled?: boolean
  phone: boolean
  trailing?: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn('flex w-full items-center text-left text-text',
        'border-t border-line-faint first:border-t-0 hover:bg-line-soft disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent',
        phone ? 'min-h-[56px] gap-3 px-4 py-2.5' : 'gap-[11px] px-3 py-[9px]')}
    >
      <span aria-hidden className={cn('flex shrink-0 items-center justify-center rounded-[8px] bg-line-soft text-text-muted',
        phone ? 'h-9 w-9' : 'h-7 w-7')}>
        {glyph}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <b className={cn('font-semibold', phone ? 'text-[15px]' : 'text-[13.5px]')}>{label}</b>
        <span className={cn('leading-[1.3] text-text-muted', phone ? 'text-[12.5px]' : 'text-[11.5px]')}>{help}</span>
      </span>
      {trailing}
      {phone && !trailing && <span aria-hidden className="shrink-0 text-text-faint"><IconChevronRight size={16} /></span>}
    </button>
  )
}
